import React from 'react';
import { motion } from 'framer-motion';
import { Eyebrow, Card } from './UI';
import { Star, ShieldCheck, ThumbsUp, MapPin, BadgeCheck } from 'lucide-react';
// Trustpilot-style filled star (acid green square with white star)
const StarBlock = ({
  size = 18
}: {
  size?: number;
}) => <span className="inline-flex items-center justify-center bg-acid-green" style={{
  width: size + 4,
  height: size + 4
}} data-id="element-583">
    <Star size={size} className="text-black" fill="currentColor" strokeWidth={0} data-id="element-584" />
  </span>;
const StarRow = ({
  count = 5,
  size = 16
}: {
  count?: number;
  size?: number;
}) => <div className="flex gap-[2px]" data-id="element-585">
    {Array.from({
    length: 5
  }).map((_, i) => <span key={i} className={`inline-flex items-center justify-center ${i < count ? 'bg-acid-green' : 'bg-dark-3'}`} style={{
    width: size + 4,
    height: size + 4
  }} data-id="element-586">
        <Star size={size} className={i < count ? 'text-black' : 'text-dark-3'} fill="currentColor" strokeWidth={0} data-id="element-587" />
      </span>)}
  </div>;
// Avatar with initials
const Avatar = ({
  name,
  hue
}: {
  name: string;
  hue: number;
}) => {
  const initials = name.split(' ').filter(p => p && !p.includes('|') && !p.includes('(')).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  return <div className="w-11 h-11 shrink-0 flex items-center justify-center font-bebas text-[16px] text-white border border-white/10" style={{
    background: `linear-gradient(135deg, hsl(${hue}, 30%, 22%) 0%, hsl(${hue}, 25%, 14%) 100%)`
  }} data-id="element-588">
      {initials}
    </div>;
};
export const SocialProof = () => {
  const reviews = [{
    title: "Toughest mask I've ever put on my face",
    body: "11 years of airsoft. Probably 15 different masks. This is the only one I've ever reviewed because it's the only one that actually earned it. Took a point-blank hit at 390 FPS. Didn't even flinch. The lenses are clearer than my $200 Oakleys. My team ordered three more.",
    name: 'Marcus R.',
    location: 'Texas, USA',
    meta: 'Verified Airsoft Competitor',
    date: 'Reviewed 14 days ago',
    helpful: 142,
    hue: 90
  }, {
    title: 'Won best costume — 200 people asked me where I got this',
    body: 'Dragon Con 2024. I had literally two hundred people stop me to ask about the mask. I printed cards pointing them to the website. Won best in show. The gold lens matched my armor like it was custom built for it. This thing is a statement piece.',
    name: 'Jordan K.',
    location: 'Atlanta, GA',
    meta: 'Cosplay Artist',
    date: 'Reviewed 3 weeks ago',
    helpful: 89,
    hue: 280
  }, {
    title: "My buddy's F-250 ran over it. I'm dead serious.",
    body: 'Parking lot incident at our field. Full-size truck, loaded, rolled the front tire over the shell. We thought it was destroyed. Picked it up — no cracks, no structural damage. Lenses were fine. We posted the photo. 47,000 impressions. The mask sold itself.',
    name: 'Derek W.',
    location: 'Nashville, TN',
    meta: 'Content Creator',
    date: 'Reviewed 1 month ago',
    helpful: 318,
    hue: 30
  }, {
    title: 'Finally built like the guys who wear it',
    body: '20-year Army veteran. I have zero patience for gear that looks tactical and performs like a toy. The BLACKOUT UNIT is the real thing. The construction, the weight distribution, the materials — this was built by someone who actually understands field equipment. Respect. I bought two more for my sons.',
    name: 'SSG D. Morrison',
    location: 'Arizona, USA',
    meta: 'US Army Veteran (Ret.)',
    date: 'Reviewed 5 weeks ago',
    helpful: 256,
    hue: 200
  }, {
    title: 'The lens swap system is pure genius',
    body: 'I switch from clear to smoke to gold depending on conditions. Takes me 20 seconds without tools. No other mask on the market does this. The anti-fog coating actually works at full sprint — that alone puts it in a completely different class.',
    name: 'Tyler M.',
    location: 'Nevada, USA',
    meta: 'Competitive Airsoft Player',
    date: 'Reviewed 6 weeks ago',
    helpful: 74,
    hue: 160
  }, {
    title: 'Four of us on the squad — everyone demanded one',
    body: 'Bought the squad bundle for our paintball team. The other team literally paused mid-round to ask us about the masks. We dominated. We looked elite. The lens swap meant we all matched colors in under two minutes. Worth every dollar — times four.',
    name: 'Brandon K.',
    location: 'Colorado, USA',
    meta: 'Paintball Team Captain',
    date: 'Reviewed 2 months ago',
    helpful: 121,
    hue: 60
  }];
  // Star distribution (Trustpilot style)
  const distribution = [{
    stars: 5,
    pct: 92
  }, {
    stars: 4,
    pct: 6
  }, {
    stars: 3,
    pct: 1
  }, {
    stars: 2,
    pct: 0.5
  }, {
    stars: 1,
    pct: 0.5
  }];
  return <section id="intel" className="bg-[#0a0a0a] pt-[40px] pb-[100px]" data-id="element-589">
      <div className="container mx-auto px-6 md:px-12" data-id="element-590">
        <div className="text-center mb-12" data-id="element-591">
          <Eyebrow data-id="element-592">FIELD INTEL</Eyebrow>
          <h2 className="text-5xl md:text-[72px] leading-[0.9] mb-4" data-id="element-593">
            OPERATORS <span className="text-acid-green" data-id="element-594">DON'T LIE.</span>
          </h2>
          <p className="font-barlow text-lg text-text-body max-w-2xl mx-auto" data-id="element-595">
            Verified reviews from operators who deployed the BLACKOUT UNIT
            Ultimate Mask in the field.
          </p>
        </div>

        {/* Trustpilot-style summary panel */}
        <div className="max-w-[1100px] mx-auto border border-dark-3 bg-dark-1 mb-12" data-id="element-596">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr] divide-y md:divide-y-0 md:divide-x divide-dark-3" data-id="element-597">
            {/* Score block */}
            <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-598">
              <div className="flex items-center gap-2 mb-3" data-id="element-599">
                <ShieldCheck size={16} className="text-acid-green" data-id="element-600" />
                <span className="font-barlow text-[10px] text-acid-green tracking-[2px] uppercase" data-id="element-601">
                  Independently Verified
                </span>
              </div>
              <div className="font-bebas text-[64px] text-white leading-none mb-3" data-id="element-602">
                4.9
                <span className="text-text-dimmed text-[28px]" data-id="element-603"> / 5.0</span>
              </div>
              <StarRow count={5} size={20} data-id="element-604" />
              <div className="font-barlow text-sm text-text-body tracking-wide mt-3" data-id="element-605">
                Based on{' '}
                <span className="text-white font-bold" data-id="element-606">9,273 reviews</span>
              </div>
              <div className="flex items-center gap-2 mt-4 text-text-dimmed" data-id="element-607">
                <BadgeCheck size={14} className="text-acid-green" data-id="element-608" />
                <span className="font-inter text-[11px]" data-id="element-609">
                  TrustScore — Excellent
                </span>
              </div>
            </div>

            {/* Distribution + secondary stats */}
            <div className="p-8" data-id="element-610">
              <div className="font-barlow text-[10px] text-acid-green tracking-[2px] uppercase mb-4" data-id="element-611">
                ━━ RATING DISTRIBUTION ━━
              </div>
              <div className="space-y-2 mb-6" data-id="element-612">
                {distribution.map(d => <div key={d.stars} className="flex items-center gap-3" data-id="element-613">
                    <div className="font-barlow text-[11px] text-text-dimmed w-12" data-id="element-614">
                      {d.stars}-star
                    </div>
                    <div className="flex-grow h-2 bg-dark-3 overflow-hidden" data-id="element-615">
                      <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: `${d.pct}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut'
                  }} className="h-full bg-acid-green" data-id="element-616" />
                    </div>
                    <div className="font-barlow text-[11px] text-white w-10 text-right" data-id="element-617">
                      {d.pct}%
                    </div>
                  </div>)}
              </div>
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-dark-3 text-center" data-id="element-618">
                <div data-id="element-619">
                  <div className="font-bebas text-2xl text-white" data-id="element-620">97%</div>
                  <div className="font-barlow text-[9px] text-text-dimmed tracking-[2px] uppercase" data-id="element-621">
                    Recommend
                  </div>
                </div>
                <div data-id="element-622">
                  <div className="font-bebas text-2xl text-white" data-id="element-623">50,287</div>
                  <div className="font-barlow text-[9px] text-text-dimmed tracking-[2px] uppercase" data-id="element-624">
                    Deployed
                  </div>
                </div>
                <div data-id="element-625">
                  <div className="font-bebas text-2xl text-white" data-id="element-626">100%</div>
                  <div className="font-barlow text-[9px] text-text-dimmed tracking-[2px] uppercase" data-id="element-627">
                    Verified
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1200px] mx-auto" data-id="element-628">
          {reviews.map((rev, i) => <Card key={i} className="p-6 flex flex-col" data-id="element-629">
              {/* Header — avatar, name, verified */}
              <div className="flex items-start gap-3 mb-4" data-id="element-630">
                <Avatar name={rev.name} hue={rev.hue} data-id="element-631" />
                <div className="flex-grow min-w-0" data-id="element-632">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-633">
                    <span className="font-barlow font-bold text-[14px] text-white truncate" data-id="element-634">
                      {rev.name}
                    </span>
                    <BadgeCheck size={14} className="text-acid-green shrink-0" data-id="element-635" />
                  </div>
                  <div className="flex items-center gap-1 font-inter text-[11px] text-text-dimmed" data-id="element-636">
                    <MapPin size={10} data-id="element-637" />
                    <span className="truncate" data-id="element-638">{rev.location}</span>
                  </div>
                </div>
              </div>

              {/* Stars + date */}
              <div className="flex items-center justify-between mb-4" data-id="element-639">
                <StarRow count={5} size={12} data-id="element-640" />
                <div className="font-inter text-[10px] text-text-dimmed" data-id="element-641">
                  {rev.date}
                </div>
              </div>

              {/* Title */}
              <h4 className="font-bebas text-[18px] text-white mb-2 leading-tight" data-id="element-642">
                {rev.title}
              </h4>

              {/* Body */}
              <p className="font-inter text-[13px] text-text-body leading-relaxed mb-5 flex-grow" data-id="element-643">
                {rev.body}
              </p>

              {/* Footer — meta + helpful */}
              <div className="flex items-center justify-between pt-4 border-t border-dark-3" data-id="element-644">
                <div className="flex items-center gap-1.5 font-barlow text-[10px] text-acid-green tracking-[1.5px] uppercase" data-id="element-645">
                  <ShieldCheck size={11} data-id="element-646" />
                  <span data-id="element-647">{rev.meta}</span>
                </div>
                <div className="flex items-center gap-1 font-inter text-[10px] text-text-dimmed" data-id="element-648">
                  <ThumbsUp size={10} data-id="element-649" />
                  <span data-id="element-650">{rev.helpful}</span>
                </div>
              </div>
            </Card>)}
        </div>
      </div>
    </section>;
};